import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FileText, Save, Edit2, ChevronDown, ChevronUp, Plus, Trash2, Clock, User, Search, Bold, Italic, List, ListOrdered, Underline, Link2, Heading1, Heading2, AlignLeft, AlignCenter, Quote, CheckSquare } from 'lucide-react';
import { toast } from 'sonner';

// Simple Rich Text Editor Component
const RichTextEditor = ({ value, onChange, placeholder }) => {
  const editorRef = useRef(null);
  const [isEmpty, setIsEmpty] = useState(!value);
  const [activeFormats, setActiveFormats] = useState({
    bold: false,
    italic: false,
    underline: false,
    insertUnorderedList: false,
    insertOrderedList: false
  });

  // Update active format states based on current selection
  const updateActiveFormats = () => {
    setActiveFormats({
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      underline: document.queryCommandState('underline'),
      insertUnorderedList: document.queryCommandState('insertUnorderedList'),
      insertOrderedList: document.queryCommandState('insertOrderedList')
    });
  };

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value || '';
      setIsEmpty(!value);
    }
  }, []);

  // Listen for selection changes to update toolbar state
  useEffect(() => {
    document.addEventListener('selectionchange', updateActiveFormats);
    return () => document.removeEventListener('selectionchange', updateActiveFormats);
  }, []);

  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    handleInput();
    updateActiveFormats();
  };

  const handleInput = () => {
    const html = editorRef.current?.innerHTML || '';
    const text = editorRef.current?.innerText || '';
    // Check if empty: no text AND no structural elements (lists, headings, etc.)
    const hasStructuralContent = /<(ul|ol|li|h1|h2|h3|blockquote|a|br)\b/i.test(html);
    setIsEmpty(!text.trim() && !hasStructuralContent);
    onChange(html);
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  };

  const insertLink = () => {
    const url = prompt('URL eingeben:');
    if (url) {
      execCommand('createLink', url);
    }
  };

  return (
    <div className="border rounded-lg overflow-hidden">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-0.5 p-2 border-b bg-muted/30">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={`h-8 w-8 p-0 ${activeFormats.bold ? 'bg-primary/20 text-primary' : ''}`}
          onClick={() => execCommand('bold')}
          title="Fett (Ctrl+B)"
        >
          <Bold className="w-4 h-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={`h-8 w-8 p-0 ${activeFormats.italic ? 'bg-primary/20 text-primary' : ''}`}
          onClick={() => execCommand('italic')}
          title="Kursiv (Ctrl+I)"
        >
          <Italic className="w-4 h-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={`h-8 w-8 p-0 ${activeFormats.underline ? 'bg-primary/20 text-primary' : ''}`}
          onClick={() => execCommand('underline')}
          title="Unterstrichen (Ctrl+U)"
        >
          <Underline className="w-4 h-4" />
        </Button>
        
        <div className="w-px h-5 bg-border mx-1" />
        
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={() => execCommand('formatBlock', '<h1>')}
          title="Überschrift 1"
        >
          <Heading1 className="w-4 h-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={() => execCommand('formatBlock', '<h2>')}
          title="Überschrift 2"
        >
          <Heading2 className="w-4 h-4" />
        </Button>
        
        <div className="w-px h-5 bg-border mx-1" />
        
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={`h-8 w-8 p-0 ${activeFormats.insertUnorderedList ? 'bg-primary/20 text-primary' : ''}`}
          onClick={() => execCommand('insertUnorderedList')}
          title="Aufzählung"
        >
          <List className="w-4 h-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={`h-8 w-8 p-0 ${activeFormats.insertOrderedList ? 'bg-primary/20 text-primary' : ''}`}
          onClick={() => execCommand('insertOrderedList')}
          title="Nummerierte Liste"
        >
          <ListOrdered className="w-4 h-4" />
        </Button>
        
        <div className="w-px h-5 bg-border mx-1" />
        
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={() => execCommand('formatBlock', '<blockquote>')}
          title="Zitat"
        >
          <Quote className="w-4 h-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={insertLink}
          title="Link einfügen"
        >
          <Link2 className="w-4 h-4" />
        </Button>
        
        <div className="w-px h-5 bg-border mx-1" />
        
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={() => execCommand('justifyLeft')}
          title="Links ausrichten"
        >
          <AlignLeft className="w-4 h-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={() => execCommand('justifyCenter')}
          title="Zentrieren"
        >
          <AlignCenter className="w-4 h-4" />
        </Button>
      </div>
      
      {/* Editor */}
      <div className="relative">
        <div
          ref={editorRef}
          contentEditable
          className="min-h-[250px] max-h-[400px] overflow-y-auto p-4 focus:outline-none prose prose-sm max-w-none
            [&_h1]:text-xl [&_h1]:font-bold [&_h1]:mt-4 [&_h1]:mb-2
            [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:mt-3 [&_h2]:mb-2
            [&_ul]:list-disc [&_ul]:ml-4 [&_ul]:my-2
            [&_ol]:list-decimal [&_ol]:ml-4 [&_ol]:my-2
            [&_li]:my-1
            [&_blockquote]:border-l-4 [&_blockquote]:border-primary/30 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-muted-foreground
            [&_a]:text-primary [&_a]:underline"
          onInput={handleInput}
          onPaste={handlePaste}
          onBlur={handleInput}
        />
        {isEmpty && (
          <div className="absolute top-4 left-4 text-muted-foreground pointer-events-none">
            {placeholder || 'Notiz schreiben...'}
          </div>
        )}
      </div>
    </div>
  );
};

const ProjectNotes = ({ projectId, tasks = [] }) => {
  const [notes, setNotes] = useState([]);
  const [isExpanded, setIsExpanded] = useState(true);
  const [loading, setLoading] = useState(true);
  const [editingNote, setEditingNote] = useState(null);
  const [newNoteOpen, setNewNoteOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [saving, setSaving] = useState(false);
  const [filterTaskId, setFilterTaskId] = useState('all');
  
  // Form state
  const [noteTitle, setNoteTitle] = useState('');
  const [noteContent, setNoteContent] = useState('');
  const [noteTaskId, setNoteTaskId] = useState('');

  useEffect(() => {
    fetchNotes();
  }, [projectId]);

  const fetchNotes = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/projects/${projectId}/notes-list`);
      setNotes(response.data || []);
    } catch (error) {
      try {
        const response = await axios.get(`${API}/projects/${projectId}/notes`);
        if (response.data.notes) {
          setNotes([{
            id: 'legacy',
            title: 'Allgemeine Notizen',
            content: response.data.notes,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }]);
        } else {
          setNotes([]);
        }
      } catch (e) {
        setNotes([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveNote = async () => {
    if (!noteTitle.trim()) {
      toast.error('Bitte einen Titel eingeben');
      return;
    }
    
    setSaving(true);
    try {
      if (editingNote) {
        await axios.put(`${API}/projects/${projectId}/notes/${editingNote.id}`, {
          title: noteTitle,
          content: noteContent,
          task_id: noteTaskId || null
        });
        toast.success('Notiz aktualisiert');
      } else {
        await axios.post(`${API}/projects/${projectId}/notes-list`, {
          title: noteTitle,
          content: noteContent,
          task_id: noteTaskId || null
        });
        toast.success('Notiz erstellt');
      }
      
      await fetchNotes();
      closeEditor();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!confirm('Notiz wirklich löschen?')) return;
    
    try {
      await axios.delete(`${API}/projects/${projectId}/notes/${noteId}`);
      toast.success('Notiz gelöscht');
      await fetchNotes();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const openNewNote = () => {
    setEditingNote(null);
    setNoteTitle('');
    setNoteContent('');
    setNoteTaskId('');
    setNewNoteOpen(true);
  };

  const openEditNote = (note) => {
    setEditingNote(note);
    setNoteTitle(note.title);
    setNoteContent(note.content);
    setNoteTaskId(note.task_id || '');
    setNewNoteOpen(true);
  };

  const closeEditor = () => {
    setNewNoteOpen(false);
    setEditingNote(null);
    setNoteTitle('');
    setNoteContent('');
    setNoteTaskId('');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-CH', { 
      day: '2-digit', 
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Strip HTML for preview
  const stripHtml = (html) => {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  // Filter notes
  const filteredNotes = notes.filter(note => {
    const plainContent = stripHtml(note.content);
    const matchesSearch = note.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         plainContent.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTask = filterTaskId === 'all' || 
                       (filterTaskId === 'project' && !note.task_id) ||
                       note.task_id === filterTaskId;
    return matchesSearch && matchesTask;
  });

  const tasksWithNotes = [...new Set(notes.filter(n => n.task_id).map(n => n.task_id))];

  return (
    <>
      <Card data-testid="project-notes-card">
        <CardHeader 
          className="cursor-pointer hover:bg-muted/50 transition-colors py-3"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <CardTitle className="text-sm font-medium flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Team-Notizen
              {notes.length > 0 && (
                <span className="text-xs bg-muted px-1.5 py-0.5 rounded-full">
                  {notes.length}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={(e) => {
                  e.stopPropagation();
                  openNewNote();
                }}
                title="Neue Notiz"
              >
                <Plus className="w-3 h-3" />
              </Button>
              {isExpanded ? (
                <ChevronUp className="w-4 h-4 text-muted-foreground" />
              ) : (
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              )}
            </div>
          </CardTitle>
        </CardHeader>

        {isExpanded && (
          <CardContent className="pt-0 space-y-2">
            {/* Filters */}
            {notes.length > 0 && (
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Suchen..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="h-7 text-xs pl-7"
                  />
                </div>
                {tasksWithNotes.length > 0 && (
                  <Select value={filterTaskId} onValueChange={setFilterTaskId}>
                    <SelectTrigger className="h-7 w-[100px] text-xs">
                      <SelectValue placeholder="Filter" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all" className="text-xs">Alle</SelectItem>
                      <SelectItem value="project" className="text-xs">Projekt</SelectItem>
                      {tasks.filter(t => tasksWithNotes.includes(t.id)).map(task => (
                        <SelectItem key={task.id} value={task.id} className="text-xs">
                          {task.title.substring(0, 15)}...
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}

            {/* Notes List */}
            {loading ? (
              <div className="text-xs text-muted-foreground text-center py-4">
                Lädt...
              </div>
            ) : filteredNotes.length === 0 ? (
              <div 
                className="text-xs text-muted-foreground text-center py-6 cursor-pointer hover:bg-muted/30 rounded-lg transition-colors"
                onClick={openNewNote}
              >
                <FileText className="w-6 h-6 mx-auto mb-2 opacity-30" />
                <p>Keine Notizen vorhanden</p>
                <p className="text-[10px] mt-1">Klicken um erste Notiz zu erstellen</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {filteredNotes.map((note) => (
                  <div 
                    key={note.id}
                    className="p-2 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors group"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div 
                        className="flex-1 min-w-0 cursor-pointer"
                        onClick={() => openEditNote(note)}
                      >
                        <div className="flex items-center gap-1.5">
                          <h4 className="text-xs font-medium truncate">{note.title}</h4>
                          {note.task_title && (
                            <span className="text-[9px] bg-primary/10 text-primary px-1 py-0.5 rounded flex items-center gap-0.5 flex-shrink-0">
                              <CheckSquare className="w-2 h-2" />
                              {note.task_title.substring(0, 10)}...
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] text-muted-foreground line-clamp-2 mt-0.5">
                          {stripHtml(note.content)?.substring(0, 80)}
                          {stripHtml(note.content)?.length > 80 && '...'}
                        </div>
                        <div className="flex items-center gap-2 mt-1 text-[10px] text-muted-foreground/70">
                          <Clock className="w-2.5 h-2.5" />
                          {formatDate(note.updated_at || note.created_at)}
                          {note.updated_by_name && (
                            <>
                              <User className="w-2.5 h-2.5 ml-1" />
                              {note.updated_by_name}
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-5 w-5"
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditNote(note);
                          }}
                          title="Bearbeiten"
                        >
                          <Edit2 className="w-2.5 h-2.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-5 w-5 text-destructive hover:text-destructive"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteNote(note.id);
                          }}
                          title="Löschen"
                        >
                          <Trash2 className="w-2.5 h-2.5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Note Editor Dialog */}
      <Dialog open={newNoteOpen} onOpenChange={closeEditor}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingNote ? 'Notiz bearbeiten' : 'Neue Notiz'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Title */}
            <Input
              placeholder="Titel der Notiz..."
              value={noteTitle}
              onChange={(e) => setNoteTitle(e.target.value)}
              className="text-lg font-medium"
            />
            
            {/* Task Selection */}
            <div className="flex items-center gap-2">
              <label className="text-sm text-muted-foreground whitespace-nowrap">
                Verknüpft mit Task:
              </label>
              <Select value={noteTaskId || "none"} onValueChange={(val) => setNoteTaskId(val === "none" ? "" : val)}>
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Kein Task (Projekt-Notiz)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Kein Task (Projekt-Notiz)</SelectItem>
                  {tasks.map(task => (
                    <SelectItem key={task.id} value={task.id}>
                      {task.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Rich Text Editor */}
            <RichTextEditor
              value={noteContent}
              onChange={setNoteContent}
              placeholder="Notiz schreiben... Formatiere mit den Buttons oben."
            />
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={closeEditor}>
              Abbrechen
            </Button>
            <Button onClick={handleSaveNote} disabled={saving}>
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Speichern...' : 'Speichern'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ProjectNotes;
